import React, { useEffect, useRef, useState, useMemo } from "react";
import { cn, sanitizeFileName } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Upload, X } from "lucide-react";

interface ChildProvisionChartProps {
  monatlicheEinzahlung?: number;
  laufzeitJahre?: number;
  /** Optional image URL for the right side illustration */
  illustrationUrl?: string;
  /** Whether edit mode is active (shows upload button) */
  isEditMode?: boolean;
  /** Callback when illustration changes */
  onIllustrationChange?: (url: string | null) => void;
  /** Callback when form values change (for persistence) */
  onFieldChange?: (fieldId: string, value: any) => void;
  className?: string;
}

// Constants for calculations based on the screenshot
const SPARBUCH_ZINS = 0.5; // 0.5% p.a.

export function ChildProvisionChart({
  monatlicheEinzahlung: initialEinzahlung = 100,
  laufzeitJahre: initialJahre = 25,
  illustrationUrl,
  isEditMode = false,
  onIllustrationChange,
  onFieldChange,
  className,
}: ChildProvisionChartProps) {
  // Local state for editable values
  const [einzahlung, setEinzahlung] = useState(initialEinzahlung);
  const [jahre, setJahre] = useState(initialJahre);
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(illustrationUrl || null);
  const [imageScale, setImageScale] = useState(100);
  const [isUploading, setIsUploading] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync from props when they change
  useEffect(() => {
    setEinzahlung(initialEinzahlung);
  }, [initialEinzahlung]);
  
  useEffect(() => {
    setJahre(initialJahre);
  }, [initialJahre]);
  
  useEffect(() => {
    setLocalImageUrl(illustrationUrl || null);
  }, [illustrationUrl]);

  const formatCurrency = (value: number) => {
    return `€ ${new Intl.NumberFormat("de-AT", {
      maximumFractionDigits: 0,
    }).format(Math.round(value))},-`;
  };

  // Calculate compound interest: FV = P * ((1 + r)^n - 1) / r * (1 + r) for monthly payments
  const calculateFutureValue = (monthlyPayment: number, years: number, annualRate: number) => {
    if (annualRate === 0) return monthlyPayment * 12 * years;
    const monthlyRate = annualRate / 100 / 12;
    const months = years * 12;
    return monthlyPayment * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
  };

  // Table data calculation matching the screenshot
  const tableData = useMemo(() => {
    const wertentwicklungen = [-3, 0, 3, 6];
    const eingezahlt = einzahlung * 12 * jahre;
    
    return wertentwicklungen.map(wert => {
      // Sparbuch - fixed rate
      const sparbuch = calculateFutureValue(einzahlung, jahre, SPARBUCH_ZINS);
      
      // Variante A - with costs (Agio 2.5%, Verwaltung 1.2%, etc.)
      // Simplified: reduce effective return by ~2.5% for costs
      const varianteARate = wert - 2.5;
      const varianteA = calculateFutureValue(einzahlung, jahre, varianteARate);
      
      // Variante B - insurance with lower costs
      // Simplified: reduce effective return by ~1.5% for costs
      const varianteBRate = wert - 1.5;
      const varianteB = calculateFutureValue(einzahlung, jahre, varianteBRate);
      
      return {
        wertentwicklung: wert,
        sparbuch: Math.round(sparbuch),
        varianteA: Math.round(varianteA),
        varianteB: Math.round(varianteB),
      };
    });
  }, [einzahlung, jahre]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const safeName = sanitizeFileName(file.name);
      const filePath = `child-provision-illustrations/${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("beratung-documents")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("beratung-documents")
        .getPublicUrl(filePath);

      const publicUrl = urlData?.publicUrl;
      if (!publicUrl) throw new Error("Konnte keine öffentliche URL erzeugen");

      setLocalImageUrl(publicUrl);
      onIllustrationChange?.(publicUrl);
    } catch (err) {
      console.error("Illustration Upload fehlgeschlagen:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setLocalImageUrl(null);
    onIllustrationChange?.(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={cn("p-6 space-y-6", className)}>
      {/* Title */}
      <div className="text-left">
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Was möchten Sie </span>
          <span className="text-primary">Ihren Kindern mitgeben?</span>
        </h2>
        <p className="text-lg italic text-muted-foreground mt-1">
          Finanzielle Weichenstellung für morgen.
        </p>
      </div>

      {/* 12-column grid layout */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left side: Table (8 columns) */}
        <div className="col-span-12 lg:col-span-8">
          <div className="border-2 border-primary rounded-lg p-4">
            {/* Table subtitle */}
            <div className="text-sm font-semibold text-foreground mb-4">
              Bsp: {einzahlung} Euro monatlich, {jahre} Jahre lang sparen (Zinseszinsformel)
            </div>

            {/* Comparison Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-foreground">
                    <th className="text-left py-2 px-3 font-medium">Sparbuch*</th>
                    <th className="text-left py-2 px-3 font-medium">Variante A*</th>
                    <th className="text-left py-2 px-3 font-medium">Variante B*</th>
                    <th className="text-left py-2 px-3 font-medium">Wertentwicklung*</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, idx) => (
                    <tr key={idx} className="border-b border-muted">
                      <td className="py-3 px-3 text-lg">{formatCurrency(row.sparbuch)}</td>
                      <td className="py-3 px-3 text-lg">{formatCurrency(row.varianteA)}</td>
                      <td className="py-3 px-3 text-lg">{formatCurrency(row.varianteB)}</td>
                      <td className="py-3 px-3 text-lg text-center">{row.wertentwicklung}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Disclaimer text */}
            <div className="mt-4 text-xs text-muted-foreground leading-relaxed">
              <p>
                *Repräsentatives Beispiel ohne Dynamik. Eingezahltes Kapital: {formatCurrency(einzahlung * 12 * jahre).replace('€ ', '').replace(',-', '')} EUR
              </p>
              <p className="mt-1">
                <strong>Variante A:</strong> Agio 2,5%, Verwaltungsgebühr 1,2% p.a., Depotgebühr 0,12% p.a., Quartalsgebühr 5,20 EUR, Transaktionsgebühr 1,1 EUR, KESt 27,5% 
                <strong> Variante B:</strong> Sparprämie (veranlagte Beträge) 82,13%, Versicherungssteuer 3,85%, Risikoprämie für das Ablebensrisiko 0,20%, Kosten 13,82%
              </p>
              <p className="mt-1">
                Hinweis: Die dargestellten Wertentwicklungen sind unverbindlich und dienen ausschließlich als Beispiel. 
                Fondskosten in Höhe von 0,12 % p.a. sowie mögliche Kosten durch Abweichungen von der geplanten Ansparphase sind nicht berücksichtigt. 
                <strong> Sparbuch</strong> 0,5% p.a. konstant
              </p>
            </div>

            {/* Dotted line separator */}
            <div className="border-t-2 border-dashed border-primary/30 my-4" />

            {/* Input field for monthly savings */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">
                Wie viel möchten Sie monatlich für Ihre Kinder ansparen?
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-lg">€</span>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={einzahlung}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    const numVal = val === '' ? 0 : Number(val);
                    setEinzahlung(numVal);
                    onFieldChange?.('kindervorsorge_einzahlung', numVal);
                  }}
                  className="w-40 h-12 text-lg"
                />
                <span className="text-lg">monatlich</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right side: Illustration Card (4 columns) */}
        <div className="col-span-12 lg:col-span-4">
          <div className="border-2 border-primary rounded-lg p-4 h-full flex flex-col items-center justify-center min-h-[400px]">
            {localImageUrl ? (
              <div className="relative w-full h-full flex flex-col items-center justify-center gap-2">
                <img 
                  src={localImageUrl} 
                  alt="Illustration" 
                  className="object-contain transition-transform rounded-lg"
                  style={{ 
                    maxWidth: `${imageScale}%`, 
                    maxHeight: `${Math.min(350, 350 * imageScale / 100)}px` 
                  }}
                />
                {isEditMode && (
                  <>
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-0 right-0 h-8 w-8"
                      onClick={handleRemoveImage}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <div className="w-full max-w-[180px] mt-2">
                      <Label className="text-xs text-muted-foreground mb-1 block">
                        Größe: {imageScale}%
                      </Label>
                      <Slider
                        value={[imageScale]}
                        onValueChange={([v]) => setImageScale(v)}
                        min={50}
                        max={300}
                        step={10}
                        className="w-full"
                      />
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center p-4 space-y-4 w-full">
                {isEditMode ? (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      disabled={isUploading}
                      className="border-dashed border-2 h-32 w-full flex flex-col gap-2"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {isUploading ? "Upload läuft..." : "Grafik hochladen"}
                      </span>
                    </Button>
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    Keine Grafik hinterlegt.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
